// Copyright (c) 2020 by Audere
//
// Use of this source code is governed by an LGPL-3.0 license that
// can be found in the LICENSE file distributed with this file.

import { CustomerConfig } from "utils/ConfigTypes";

export const AudereConfig: CustomerConfig = {
  id: "audere",
  name: "Audere",
  pathPrefix: "audere",
  allowGuestRuns: false,
  landingPage: "CHOOSEPROFILE",
  customThemeOverride: {},
  headerConfig: {
    headerLogo: { label: "Audere", position: "center" },
  },
  loginImageUrl: "AudereWhiteLogo.png",
  loginHeader:
    "Audere OpenRDT enables you to order and conduct a COVID-19 test from the comfort of your home.",
  loginDescription:
    "The COVID-19 Antibody Home Testing App is offered by Audere, and enables you to order and conduct a COVID-19 antibody test from the comfort of your home.",
  showProgressBar: true,

  getTestRunDefinition: () => ({
    testkitIntro: {
      contentDescription: [
        {
          type: "DemoAsset",
          props: {
            height: 200,
            width: 320,
            type: "local",
            alt: "Collection kit image",
            src: "healthpulse/start.png",
          },
          display: "centered",
        },
        {
          type: "Title",
          props: {
            content: "HealthPulse@home COVID-19 Specimen Collection Kit",
          },
          display: "centered",
        },
        {
          type: "NextStepButton",
          props: { text: "Start" },
          display: "centered",
        },
        {
          type: "Paragraph",
          props: {
            content: `The collection process should take less than 10 minutes.`,
          },
          display: "centered",
        },
      ],
      nav: {
        next: {
          default: "gettingStarted",
        },
      },
    },
    gettingStarted: {
      contentDescription: [
        {
          type: "Asset",
          props: {
            height: 200,
            width: 320,
            type: "local",
            alt: "Image of pen, lamp, and kit",
            src: "healthpulse/getstarted.png",
          },
          display: "centered",
        },
        {
          type: "Title",
          props: {
            content: "Let's get started",
          },
        },
        {
          type: "TestRunWarningDialog",
          props: {
            children: [
              "Please note: You must be 18 or older to use this collection kit.",
              "Additionally, if you feel that you are seriously ill, please seek immediate medical care from a healthcare professional.",
              "If you feel like you are having a medical emergency, please call 9\u20111\u20111.",
              "Signs of a serious medical condition include, but are not limited to: severe shortness of breath or difficulty breathing, coughing up blood, chest pain, irregular heartbeat, persistent vomiting or diarrhea.",
            ],
          },
        },
        {
          type: "Paragraph",
          props: {
            children: {
              type: "MarkDown",
              props: {
                source: `We will guide you through completing a nasal swab collection. Before you get started, please **have a pen handy** and place the kit on a clean, dry surface. 
                `,
              },
            },
          },
        },
        {
          type: "Paragraph",
          props: {
            children: {
              type: "MarkDown",
              props: {
                source: `It's important that you complete the test steps as **carefully** 
                  as possible to ensure the specimen can be processed in the lab.`,
              },
            },
          },
        },
        {
          type: "Paragraph",
          props: {
            children: {
              type: "MarkDown",
              props: {
                source: `[About the lab test](~aboutResultsFromStarting)`,
              },
            },
          },
        },
        {
          type: "NextStepLink",
          props: {},
        },
      ],
      nav: {
        next: {
          default: "enterBarcode",
          aboutResultsFromStarting: "aboutResults",
        },
        previous: "testkitIntro",
      },
    },
    captureBarcode: {
      contentDescription: [
        {
          type: "BarcodePanel",
          props: {},
        },
      ],
      nav: {
        previous: "enterBarcode",
      },
    },
    aboutResults: {
      contentDescription: [
        {
          type: "Title",
          props: {
            content: "What will my COVID-19 test results tell me?",
          },
        },
        {
          type: "Paragraph",
          props: {
            content:
              "Your test results will indicate whether or not you likely have COVID-19.",
          },
        },
        {
          type: "DeclarativeList",
          props: {
            items: [
              {
                type: "MarkDown",
                props: {
                  source: `If your results are **positive**, the virus was detected in the specimen. If positive, it is important to limit your exposure to other people and continue to monitor your symptoms.`,
                },
              },
              {
                type: "MarkDown",
                props: {
                  source: `If your results are **negative**, the virus was not detected in the specimen. However, it is important to note that the virus may not be detected by the test in early stages of infection. Furthermore, the virus may not be detected if the specimen is not collected as directed. If you have reason to believe you have been exposed, please follow up with a healthcare provider for further evaluation or to get retested.*`,
                },
              },
            ],
          },
        },
        {
          type: "Paragraph",
          props: {
            content:
              "You will receive an email when your test results are ready and will have an opportunity to speak with a licensed physician, who can answer any questions you may have about your test results and help determine next steps in care.",
          },
        },
        {
          type: "Paragraph",
          props: {
            content:
              "*Although the possibility is low, a false negative result is possible. A false negative is when you are actually infected with COVID-19, but your test result is negative. A false negative should be considered if you have had recent exposure to the virus along with symptoms consistent with COVID-19. The possibility of a false negative result should especially be considered if your recent exposure or symptoms suggest that COVID-19 is likely, and diagnostic tests for other respiratory illnesses are negative.",
          },
        },
        {
          type: "NextStepLink",
          props: { returnStep: true, text: "Return to test" },
        },
      ],
      nav: {
        previous: "endTest",
      },
    },
    enterBarcode: {
      contentDescription: [
        {
          type: "Asset",
          props: {
            height: 200,
            width: 320,
            type: "local",
            alt:
              "Image of kit with arrows pointing to registration ID and barcode locations",
            src: "healthpulse/registerkit.png",
          },
          display: "centered",
        },
        {
          type: "Title",
          props: {
            content: "Register kit",
          },
        },
        {
          type: "Paragraph",
          props: {
            content: `Enter the registration ID and barcode from your box. The specimen cannot be processed in the lab unless it is properly registered.`,
          },
        },
        {
          type: "RegistrationPanel",
          props: {},
        },
        {
          type: "Paragraph",
          props: {
            children: {
              type: "MarkDown",
              props: {
                source: `No barcode or registration ID? [Contact us](https://healthpulsenow.org/contact).`,
              },
            },
          },
        },
        {
          type: "NextStepButton",
          props: { text: "Register" },
        },
      ],
      hasFormContent: true,
      nav: {
        next: {
          default: "endTest",
          captureBarcode: "captureBarcode",
        },
        previous: "gettingStarted",
      },
    },

    endTest: {
      contentDescription: [
        {
          type: "Asset",
          props: {
            height: 200,
            width: 320,
            type: "local",
            alt: "Image of box, clipboard, microscope. and smartphone",
            src: "healthpulse/thankyou.png",
          },
          display: "centered",
        },
        {
          type: "Title",
          props: {
            content: "Thank you",
          },
        },
        {
          type: "Paragraph",
          props: {
            content: `You successfully completed your HealthPulse@home Specimen 
              Collection Kit. Please put any remaining packaging in the garbage.`,
          },
        },
        {
          type: "Paragraph",
          props: {
            content: `Here is what will happen next:`,
          },
        },
        {
          type: "DeclarativeList",
          props: {
            items: [
              // TODO: make ordered list
              `Once the specimen is received at the lab, it will be checked 
              and processed. We will reach out if there are any issues.`,
              `You will be contacted when your results are ready.`,
            ],
          },
        },
        {
          type: "Paragraph",
          props: {
            content: `If your symptoms worsen, or you feel 
            that you are seriously ill, please seek immediate medical care 
            from a healthcare professional.`,
          },
        },
        {
          type: "Paragraph",
          props: {
            content: `If you feel like you are having a medical emergency, 
            please call 9\u20111\u20111.`,
          },
        },
        {
          type: "Paragraph",
          props: {
            content: `Signs of a serious medical condition include, but are 
            not limited to: severe shortness of breath or difficulty breathing, 
            coughing up blood, chest pain, irregular heartbeat, persistent 
            vomiting or diarrhea.`,
          },
        },
        {
          type: "Paragraph",
          props: {
            children: {
              type: "MarkDown",
              props: {
                source: `[What will I learn from my test results?](~default)`,
              },
            },
          },
        },
        {
          type: "NavButton",
          props: { text: "Done", route: "CHOOSEPROFILE" },
        },
      ],
      nav: {
        next: {
          default: "aboutResults",
        },
        previous: "enterBarcode",
      },
    },
  }),
};

export default AudereConfig;
